import { groupEquipmentItemsByContractor, groupWorkforceItemsByContractor } from "@entities/daily-log";
import { z } from "zod";

const dailyLogTagSchema = z.object({
  id: z.number(),
  name: z.string(),
});

const dailyLogResourceItemSchema = z.object({
  contractor_id: z.number(),
  contractor_name: z.string(),
  trade_id: z.number(),
  trade_name: z.string(),
  quantity: z.number(),
});

const dailyLogResourceGroupSchema = z.object({
  total_quantity: z.number(),
  items: z.array(dailyLogResourceItemSchema),
});

const userSchema = z.object({
  id: z.number(),
  name: z.string(),
  avatar_url: z.string().nullable(),
});

const dailyLogImageSchema = z.object({
  id: z.number(),
  image_upload_path: z.string(),
  webp_image_upload_path: z.string().nullable(),
  webp_image_upload_path_resized: z.string().nullable(),
});

export const dailyLogDetailResponseSchema = z.object({
  is_owner: z.boolean(),
  modified_at: z.string(),
  id: z.number(),
  status: z.string(),
  project_id: z.number(),
  project_name: z.string(),
  description: z.string(),
  // 작성자가 탈퇴·삭제됐거나 BE가 연결을 찾지 못하면 null 이 온다. 임의 user id 를 대입하거나
  // 현재 사용자로 대체하지 않는다 — UI 는 "Unknown writer" 로 표시한다.
  user: userSchema.nullable(),
  location: z.string(),
  date: z.string(),
  d_day: z.string(),
  weather: z.string(),
  temperature: z.string(),
  temperature_unit: z.string(),
  precipitation: z.string(),
  precipitation_unit: z.string(),
  process_rate: z.string(),
  tags: z.array(dailyLogTagSchema),
  workforces: dailyLogResourceGroupSchema,
  equipments: dailyLogResourceGroupSchema,
  images: z.array(dailyLogImageSchema),
});

const commentSchema = z.object({
  id: z.number(),
  user: userSchema,
  content: z.string(),
  is_owner: z.boolean(),
  write_time: z.string(),
});

export const commentsResponseSchema = z.object({
  results: z.array(commentSchema),
  count: z.number(),
  total_pages: z.number(),
  current_page: z.number(),
});

export const fileHistoryEventType = z.enum([
  "DAILY_LOG.CREATED",
  "DAILY_LOG.MODIFIED",
  "DAILY_LOG.DELETED",
  "NOTIFICATION.SENT",
  "COMMENT.CREATED",
  "COMMENT.MODIFIED",
  "COMMENT.DELETED",
]);

const fileHistoryEventSchema = z.object({
  id: z.number(),
  user: userSchema,
  type: fileHistoryEventType,
  created_at: z.string(),
});

export const fileHistoryResponseSchema = z.object({
  results: z.array(fileHistoryEventSchema),
  count: z.number(),
  total_pages: z.number(),
  current_page: z.number(),
});

export const dailyLogDetailToFormSchema = dailyLogDetailResponseSchema.transform((detail) => ({
  description: detail.description,
  location: detail.location,
  date: detail.date,
  weather: detail.weather,
  temperature: Number(detail.temperature) || 0,
  precipitation: Number(detail.precipitation) || 0,
  processRate: Number(detail.process_rate) || 0,
  tags: detail.tags,
  workforce_contractors: groupWorkforceItemsByContractor(detail.workforces.items),
  equipment_contractors: groupEquipmentItemsByContractor(detail.equipments.items),
  image_urls: detail.images.map((img) => img.webp_image_upload_path ?? img.image_upload_path),
}));

export type DailyLogTag = z.infer<typeof dailyLogTagSchema>;
export type DailyLogResource = z.infer<typeof dailyLogResourceItemSchema>;
export type DailyLogDetailResponse = z.infer<typeof dailyLogDetailResponseSchema>;
export type Comment = z.infer<typeof commentSchema>;
export type CommentsResponse = z.infer<typeof commentsResponseSchema>;
export type FileHistoryEventType = z.infer<typeof fileHistoryEventType>;
export type FileHistoryEvent = z.infer<typeof fileHistoryEventSchema>;
export type FileHistoryResponse = z.infer<typeof fileHistoryResponseSchema>;
